import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Switch,
  CircularProgress,
  Button,
} from "@mui/material";

import {
  fetchTiposPorCategoria,
  atualizarStatusTipo,
  atualizarRequerImagem,
} from "../../api/apiService";

function GerenciarTipos() {
  const { categoria } = useParams();
  const navigate = useNavigate();
  const [tipos, setTipos] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [mostrarDesativados, setMostrarDesativados] = useState(false);
  const [categoriaSelecionada, setCategoriaSelecionada] = useState(categoria);
  const [carregando, setCarregando] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(null);

  const carregarTipos = useCallback(
    async (signal) => {
      try {
        setCarregando(true);
        const data = await fetchTiposPorCategoria(
          categoriaSelecionada,
          !mostrarDesativados,
          { signal }
        );
        setTipos(data);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Erro ao buscar tipos:", error);
        }
      } finally {
        setCarregando(false);
      }
    },
    [categoriaSelecionada, mostrarDesativados]
  );

  useEffect(() => {
    const controller = new AbortController();
    carregarTipos(controller.signal);
    return () => controller.abort();
  }, [carregarTipos]);

  const alterarStatusTipo = async (id) => {
    try {
      setLoadingStatus(id);
      await atualizarStatusTipo(id);
      carregarTipos();
    } catch (error) {
      console.error("Erro ao alterar status do tipo:", error);
    } finally {
      setLoadingStatus(null);
    }
  };

  const handleCategoriaChange = (e) => {
    const novaCategoria = e.target.value;
    setCategoriaSelecionada(novaCategoria);
    setMostrarDesativados(false);
    navigate(`/servicos/gerenciar/${novaCategoria}`);
  };

  const handleToggleChange = () => {
    setMostrarDesativados((prevState) => !prevState);
  };

  const tiposFiltrados = tipos.filter((tipo) =>
    tipo.nome.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const alterarRequerImagem = async (id) => {
    try {
      setLoadingStatus(id);
      await atualizarRequerImagem(id);
      carregarTipos();
    } catch (error) {
      console.error("Erro ao alterar requer imagem:", error);
    } finally {
      setLoadingStatus(null);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: "#f5f5f5",
        minHeight: "100vh",
        p: 4,
        marginTop: "7vh",
        "@media (max-width: 720px)": {
          marginTop: "5vh",
          p: 2, // Reduz padding no mobile
        },
      }}
    >
      <Navbar />
      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: 2,
          p: 4,
          boxShadow: 3,
          maxWidth: "900px", // Limita a largura no desktop
          mx: "auto",
          "@media (max-width: 720px)": {
            p: 3, // Ajusta padding no mobile
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between", // Ajusta os itens
            gap: 2,
            mb: 4,
            "@media (max-width: 600px)": {
              flexDirection: "column", // Empilha no mobile
              gap: 3,
            },
          }}
        >
          <Select
            value={categoriaSelecionada}
            onChange={handleCategoriaChange}
            size="small"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="CONSULTA">Consulta</MenuItem>
            <MenuItem value="EXAME">Exame</MenuItem>
            <MenuItem value="TRANSPORTE">Transporte</MenuItem>
          </Select>
          <TextField
            label="Buscar por nome"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={mostrarDesativados}
                onChange={handleToggleChange}
                color="primary"
              />
            }
            label="Mostrar Desativados"
          />
        </Box>

        {carregando ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              mt: 4,
            }}
          >
            <CircularProgress />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Carregando...
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gap: 2,
              "@media (max-width: 720px)": {
                gap: 1.5, // Reduz espaço entre itens no mobile
              },
            }}
          >
            {tiposFiltrados.map((tipo) => (
              <Box
                key={tipo.id}
                sx={{
                  p: 2,
                  border: "1px solid #ccc",
                  borderRadius: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <Typography sx={{ flex: 1 }}>
                  <strong>Nome:</strong> {tipo.nome}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    alignItems: "center",
                    justifyContent: { xs: "space-between", sm: "flex-end" },
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  <Button
                    variant={tipo.ativo ? "contained" : "outlined"}
                    color={tipo.ativo ? "success" : "error"}
                    onClick={() => alterarStatusTipo(tipo.id)}
                    disabled={loadingStatus === tipo.id}
                    size="small"
                    sx={{
                      minWidth: "100px",
                    }}
                  >
                    {loadingStatus === tipo.id ? (
                      <CircularProgress size={20} />
                    ) : tipo.ativo ? (
                      "Desativar"
                    ) : (
                      "Ativar"
                    )}
                  </Button>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={tipo.requerImagem}
                        onChange={() => alterarRequerImagem(tipo.id)}
                        color="primary"
                      />
                    }
                    label="Requer Imagem"
                  />
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default GerenciarTipos;
